import React, { useState, useEffect, useMemo } from 'react';
import { 
    CheckCircle2, 
    Clock, 
    Search, 
    Users, 
    AlertTriangle, 
    Loader2, 
    WifiOff, 
    XCircle, 
    Save, 
    Edit, 
    GraduationCap, 
    BookOpen, 
    Calendar,
    LogOut,
    Lock,
    Mail,
    Eye,
    EyeOff,
    Sun,
    Moon
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase, fetchUserProfile } from '../lib/supabase';
import useCalendarStore from '../store/useCalendarStore';

const API = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_CONFIG = {
    present: {
        label: 'Present',
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-950/20',
        border: 'border-emerald-200 dark:border-emerald-900/30',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
        rowBorder: 'border-emerald-300 dark:border-emerald-800/40',
        rowBg: 'bg-emerald-50/50 dark:bg-emerald-950/10',
        avatarBg: 'bg-emerald-200 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    },
    absent: {
        label: 'Absent',
        color: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-50 dark:bg-rose-950/20',
        border: 'border-rose-200 dark:border-rose-900/30',
        icon: <XCircle className="w-3.5 h-3.5" />,
        rowBorder: 'border-rose-300 dark:border-rose-800/40',
        rowBg: 'bg-rose-50/50 dark:bg-rose-950/10',
        avatarBg: 'bg-rose-200 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    },
    late: {
        label: 'Late',
        color: 'text-primary dark:text-zinc-200',
        bg: 'bg-primary/5 dark:bg-zinc-800/40',
        border: 'border-primary/20 dark:border-zinc-700/50',
        icon: <Clock className="w-3.5 h-3.5" />,
        rowBorder: 'border-primary/30 dark:border-zinc-700/50',
        rowBg: 'bg-primary/5 dark:bg-zinc-800/10',
        avatarBg: 'bg-primary/20 text-primary dark:bg-zinc-850 dark:text-zinc-300'
    }
};

// ─── Student Row ─────────────────────────────────────────
const StudentRow = React.memo(function StudentRow({ student, status, isPending, onToggle, isEditing }) {
    const config = STATUS_CONFIG[status];
    
    return (
        <button
            onClick={() => onToggle(student.id)}
            className={`
                w-full flex items-center gap-3 p-3.5 rounded-2xl transition-all duration-200 text-left border
                ${isEditing ? 'active:scale-[0.98]' : 'cursor-default'}
                ${status
                    ? `${config.rowBg} ${config.rowBorder}`
                    : 'bg-white border-zinc-100 hover:bg-zinc-50 dark:bg-zinc-900 dark:border-zinc-800 dark:hover:bg-zinc-850'
                }
                ${isPending ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-zinc-950' : ''}
            `}
        >
            {/* Avatar / Icon */}
            <div className={`
                w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm shrink-0 transition-all
                ${status ? config.avatarBg : 'bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-550'}
            `}>
                {status ? config.icon : student.name.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm truncate ${status ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-800 dark:text-zinc-200'}`}>
                    {student.name}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-[9px] text-zinc-400 dark:text-zinc-550 font-black uppercase tracking-widest leading-none">
                        {student.enrollment_number || 'STU-ID'}
                    </p>
                    {isPending && (
                        <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    )}
                </div>
            </div>

            {/* Status Badge */}
            <div className="shrink-0">
                {status ? (
                    <div className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${config.border} ${config.bg} ${config.color}`}>
                        {config.label}
                    </div>
                ) : (
                    <div className="px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border border-dashed border-zinc-200 dark:border-zinc-700 text-zinc-300 dark:text-zinc-600">
                        Mark
                    </div>
                )}
            </div>
        </button>
    );
});

// ─── Main Page ───────────────────────────────────────────
export default function PublicAttendancePage() {
    const darkMode = useCalendarStore(s => s.darkMode);
    const setDarkMode = useCalendarStore(s => s.setDarkMode);

    const [session, setSession] = useState(null);
    const [records, setRecords] = useState({});
    const [pendingChanges, setPendingChanges] = useState({}); // student_id -> status
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // all, present, absent, late, unmarked

    // Active Portal state
    const [currentAdmin, setCurrentAdmin] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);

    // Filter selectors in Active Portal Mode
    const [batches, setBatches] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState('');
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    const [selectedDate, setSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA'));
    const [columnId, setColumnId] = useState(null);

    // Extract token from ?token=...
    const token = useMemo(() => {
        return new URLSearchParams(window.location.search).get('token');
    }, []);

    // 1. Initial Authentication & Data loading check
    useEffect(() => {
        if (token) {
            // Token mode: fetch public session info
            fetch(`${API}/api/attendance/public/session?token=${encodeURIComponent(token)}`)
                .then(async (res) => {
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.detail || 'Failed to load session');
                    return data;
                })
                .then((data) => {
                    setSession(data);
                    setRecords(data.records || {});
                    setLoading(false);
                })
                .catch((err) => {
                    setError(err.message);
                    setLoading(false);
                });
        } else {
            // Active Portal mode: Check localstorage profile first
            const profile = JSON.parse(localStorage.getItem('admin_profile') || 'null');
            if (profile) {
                setCurrentAdmin(profile);
                loadActivePortalBaseData(profile.team_id);
            } else {
                setLoading(false);
            }
        }
    }, [token]);

    // Fetch batches/subjects in Active Portal Mode
    const loadActivePortalBaseData = async (teamId) => {
        setLoading(true);
        try {
            const [bRes, sRes] = await Promise.all([
                supabase.from('batches').select('*').eq('team_id', teamId).order('name'),
                supabase.from('subjects').select('*').eq('team_id', teamId).order('name')
            ]);

            if (bRes.error) throw bRes.error;
            if (sRes.error) throw sRes.error;

            setBatches(bRes.data || []);
            setSubjects(sRes.data || []);

            // Set initial selected values if available
            if (bRes.data?.length > 0) {
                setSelectedBatchId(bRes.data[0].id);
            }
        } catch (err) {
            console.error('Failed to load active portal metadata:', err);
            toast.error('Failed to load portal configuration data.');
        } finally {
            setLoading(false);
        }
    };

    // Filter subjects based on selected batch
    const filteredSubjects = useMemo(() => {
        const batch = batches.find(b => b.id === selectedBatchId);
        if (!batch) return subjects;
        return subjects.filter(s => s.course_id === batch.course_id || !batch.course_id);
    }, [batches, subjects, selectedBatchId]);

    // Select first subject automatically when batch or subjects change
    useEffect(() => {
        if (!token && selectedBatchId && filteredSubjects.length > 0) {
            const hasMatch = filteredSubjects.some(s => s.id === selectedSubjectId);
            if (!hasMatch) {
                setSelectedSubjectId(filteredSubjects[0].id);
            }
        }
    }, [selectedBatchId, filteredSubjects, token]);

    // Fetch or initialize attendance column for selected Active Portal parameters
    useEffect(() => {
        if (token || !selectedBatchId || !selectedSubjectId || !selectedDate || !currentAdmin) return;

        let active = true;
        const fetchColumn = async () => {
            try {
                // 1. Check if column exists
                const { data: cols, error: colErr } = await supabase
                    .from('attendance_records')
                    .select('*')
                    .eq('team_id', currentAdmin.team_id)
                    .eq('batch_id', selectedBatchId)
                    .eq('subject_id', selectedSubjectId)
                    .eq('date', selectedDate)
                    .limit(1);

                if (colErr) throw colErr;

                // 2. Fetch student list for selected batch
                const { data: stus, error: stuErr } = await supabase
                    .from('students')
                    .select('*')
                    .eq('team_id', currentAdmin.team_id)
                    .eq('batch_id', selectedBatchId)
                    .order('name');

                if (stuErr) throw stuErr;

                if (!active) return;

                const dbCol = cols?.[0];
                const batch = batches.find(b => b.id === selectedBatchId);
                const subject = subjects.find(s => s.id === selectedSubjectId);

                // Build a mock "session" matching Token mode format
                setSession({
                    column_id: dbCol?.id || null,
                    label: dbCol?.label || `${subject?.name || 'Class'} - ${selectedDate}`,
                    students: stus || []
                });

                if (dbCol) {
                    setColumnId(dbCol.id);
                    setRecords(dbCol.attendance_data || {});
                } else {
                    setColumnId(null);
                    setRecords({});
                }
                setPendingChanges({});
            } catch (err) {
                console.error(err);
                toast.error('Failed to sync student attendance list.');
            }
        };

        fetchColumn();
        return () => { active = false; };
    }, [selectedBatchId, selectedSubjectId, selectedDate, currentAdmin, token, batches, subjects]);

    // Handle inline login
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Please enter email and password');
            return;
        }

        setAuthLoading(true);
        try {
            const { data, error } = await supabase.auth.signInWithPassword({ email, password });
            if (error) throw error;

            const profile = await fetchUserProfile(data.user);
            if (!profile) {
                await supabase.auth.signOut();
                throw new Error('This account is not registered to any admin team.');
            }

            // Save user profile for RBAC mapping
            const profileData = {
                admin_id: profile.email || profile.username,
                name: profile.name,
                id: profile.id,
                team_id: profile.team_id,
                access_level: profile.access_level,
                role: profile.role,
                branch_id: profile.branch_id,
                batch_id: profile.batch_id,
                faculty_id: profile.faculty_id,
            };

            localStorage.setItem('admin_profile', JSON.stringify(profileData));
            setCurrentAdmin(profileData);
            toast.success(`Authenticated as ${profile.name || profile.email}`);
            
            // Load base data
            await loadActivePortalBaseData(profile.team_id);
        } catch (err) {
            toast.error(err.message || 'Login failed. Please check credentials.');
        } finally {
            setAuthLoading(false);
        }
    };

    // Logout admin
    const handleLogout = async () => {
        await supabase.auth.signOut();
        localStorage.removeItem('admin_profile');
        setCurrentAdmin(null);
        setSession(null);
        setBatches([]);
        setSubjects([]);
        setSelectedBatchId('');
        setSelectedSubjectId('');
        toast.info('Logged out from active portal mode');
    };

    // Handle toggling state (Present -> Absent -> Late -> Unmarked)
    const handleToggle = React.useCallback((studentId) => {
        if (!isEditing) {
            toast.error("Please click 'Edit Attendance' first to make changes.");
            return;
        }
        setPendingChanges(prev => {
            const currentStatus = prev[studentId] !== undefined ? prev[studentId] : (records[studentId] || null);
            
            let nextStatus = null;
            if (!currentStatus) nextStatus = 'present';
            else if (currentStatus === 'present') nextStatus = 'absent';
            else if (currentStatus === 'absent') nextStatus = 'late';
            else nextStatus = null;

            const newChanges = { ...prev, [studentId]: nextStatus };
            if (nextStatus === (records[studentId] || null)) {
                delete newChanges[studentId];
            }
            return newChanges;
        });
    }, [records, isEditing]);

    // Save modifications
    const handleSave = React.useCallback(async () => {
        if (saving || !session) return;
        
        setSaving(true);
        try {
            // Build the complete details for all students in the batch
            const finalRecords = { ...records };
            session.students.forEach(student => {
                const status = pendingChanges[student.id] !== undefined 
                    ? pendingChanges[student.id] 
                    : records[student.id];
                if (!status) {
                    delete finalRecords[student.id];
                } else {
                    finalRecords[student.id] = status;
                }
            });

            if (token) {
                // Token Mode: Save using API endpoint
                const marks = session.students.map(student => ({
                    student_id: student.id,
                    status: finalRecords[student.id] || null
                }));

                const res = await fetch(`${API}/api/attendance/public/bulk-mark`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token, marks }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || 'Failed to save attendance');
            } else {
                // Active Portal Mode: Save directly using Supabase client
                if (columnId) {
                    // Update existing column
                    const { error } = await supabase
                        .from('attendance_records')
                        .update({ attendance_data: finalRecords })
                        .eq('id', columnId)
                        .eq('team_id', currentAdmin.team_id);
                    
                    if (error) throw error;
                } else {
                    // Insert new column
                    const subject = subjects.find(s => s.id === selectedSubjectId);
                    const generatedLabel = `${subject?.name || 'Class'} - ${selectedDate}`;

                    const { data: inserted, error } = await supabase
                        .from('attendance_records')
                        .insert([{
                            team_id: currentAdmin.team_id,
                            batch_id: selectedBatchId,
                            subject_id: selectedSubjectId,
                            date: selectedDate,
                            label: generatedLabel,
                            attendance_data: finalRecords
                        }])
                        .select()
                        .single();

                    if (error) throw error;
                    setColumnId(inserted.id);
                    setSession(prev => ({ ...prev, column_id: inserted.id }));
                }
            }

            setRecords(finalRecords);
            setPendingChanges({});
            setIsEditing(false);
            toast.success('Attendance saved successfully!');
        } catch (err) {
            toast.error(err.message || 'Failed to save attendance');
        } finally {
            setSaving(false);
        }
    }, [pendingChanges, records, session, saving, token, columnId, currentAdmin, selectedBatchId, selectedSubjectId, selectedDate, subjects]);

    // Student filters & search logic
    const studentList = useMemo(() => {
        if (!session) return [];
        return session.students.filter(s => {
            const status = pendingChanges[s.id] !== undefined 
                ? (pendingChanges[s.id] || 'unmarked') 
                : (records[s.id] || 'unmarked');
            
            const matchesFilter = activeFilter === 'all' || 
                               (activeFilter === 'unmarked' ? status === 'unmarked' : status === activeFilter);
            
            const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase()) ||
                                (s.enrollment_number || '').toLowerCase().includes(search.toLowerCase());
            
            return matchesFilter && matchesSearch;
        });
    }, [session, search, records, pendingChanges, activeFilter]);

    // Calculate aggregated statistics
    const counts = useMemo(() => {
        const allRecords = { ...records };
        Object.keys(pendingChanges).forEach(k => {
            if (pendingChanges[k] === null) {
                delete allRecords[k];
            } else {
                allRecords[k] = pendingChanges[k];
            }
        });

        const res = { present: 0, absent: 0, late: 0, total: session?.students.length || 0 };
        Object.values(allRecords).forEach(s => {
            if (res[s] !== undefined) res[s]++;
        });
        return res;
    }, [records, pendingChanges, session]);

    // ─── Loading State ─────────────────────────────────────
    if (loading) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                <p className="text-zinc-400 dark:text-zinc-500 font-bold lowercase tracking-widest text-[10px]">Initializing Session</p>
            </div>
        );
    }

    // ─── Token Error View ──────────────────────────────────
    if (token && error) {
        const isExpired = error.toLowerCase().includes('expired');
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex flex-col items-center justify-center p-8 text-center">
                <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mb-6 rotate-12 shadow-2xl ${isExpired ? 'bg-amber-100 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400' : 'bg-rose-100 text-rose-600 dark:bg-rose-955/20 dark:text-rose-400'}`}>
                    {isExpired ? <Clock className="w-10 h-10" /> : <AlertTriangle className="w-10 h-10" />}
                </div>
                <h1 className="text-2xl font-black text-zinc-900 dark:text-zinc-100 mb-2">
                    {isExpired ? 'Link Expired' : 'Access Error'}
                </h1>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed mb-8">{error}</p>
                <div className="w-full max-w-xs h-1 bg-zinc-200 dark:bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-primary w-1/3 animate-pulse" />
                </div>
            </div>
        );
    }

    // ─── Active Portal Login Page ──────────────────────────
    if (!token && !currentAdmin) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-4 relative overflow-hidden">
                {/* Visual Glow Orbs */}
                <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

                <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl w-full max-w-md rounded-3xl shadow-xl border border-zinc-200/50 dark:border-zinc-800/80 overflow-hidden p-6 sm:p-8 space-y-6 relative z-10">
                    <div className="flex justify-end">
                        <button 
                            onClick={() => setDarkMode(!darkMode)}
                            className="p-2 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                        >
                            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                    </div>

                    <div className="text-center space-y-2">
                        <div className="mx-auto w-12 h-12 bg-primary/10 text-primary rounded-2xl flex items-center justify-center">
                            <GraduationCap className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Administrator Access</h2>
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 leading-relaxed">
                            ScholarsAnchor Active Portal. Authenticate to sync attendance.
                        </p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 pl-1">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pl-0.5" />
                                <input
                                    type="email"
                                    placeholder="admin@scholarsanchor.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-11 pr-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 rounded-xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-zinc-800 dark:text-zinc-100 text-sm"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 pl-1">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pl-0.5" />
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-11 pr-11 py-3 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 rounded-xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-zinc-800 dark:text-zinc-100 text-sm"
                                    required
                                />
                                <button 
                                    type="button" 
                                    onClick={() => setShowPw(!showPw)} 
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 p-1"
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={authLoading}
                            className="w-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold py-3 rounded-xl shadow-lg shadow-primary/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2 text-sm"
                        >
                            {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {authLoading ? 'Authenticating...' : 'Sign In to Portal'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ─── Portal Main Workspace (Dual Mode) ────────────────
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-zinc-950 pb-32">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-white/95 dark:bg-zinc-900/90 backdrop-blur-xl border-b border-zinc-200/50 dark:border-zinc-800/80">
                <div className="max-w-lg mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-1.5 mb-1">
                                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                                <p className="text-[10px] text-zinc-400 dark:text-zinc-550 font-black uppercase tracking-widest leading-none">
                                    {token ? 'Attendance Portal' : 'Active Attendance Workspace'}
                                </p>
                            </div>
                            <h1 className="font-black text-base sm:text-xl text-zinc-900 dark:text-zinc-50 leading-tight truncate">
                                {session?.label || 'Loading session...'}
                            </h1>
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                            {/* Theme Toggle */}
                            <button 
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-2 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-750 transition-colors"
                            >
                                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                            </button>

                            {/* Logout Action (Active Mode Only) */}
                            {!token && currentAdmin && (
                                <button 
                                    onClick={handleLogout}
                                    className="p-2 rounded-xl bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                                    title="Sign Out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            )}

                            {token && (
                                <div className="text-right pl-2 border-l border-zinc-200 dark:border-zinc-800">
                                    <p className="text-xl sm:text-2xl font-black text-primary leading-none tracking-tighter">
                                        {counts.present}<span className="text-zinc-300 dark:text-zinc-700 font-light mx-0.5">/</span>{counts.total}
                                    </p>
                                    <p className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest leading-none mt-1">Present</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Progress Slider (Only show when session data is loaded) */}
                    {session && (
                        <div className="mt-4 h-1 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden flex">
                            <div
                                className="h-full bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                                style={{ width: counts.total > 0 ? `${(counts.present / counts.total) * 100}%` : '0%' }}
                            />
                        </div>
                    )}
                </div>
            </header>

            <div className="max-w-lg mx-auto px-4 sm:px-6 mt-6 space-y-6">
                
                {/* ── Active Portal Selection Panel ──────────────── */}
                {!token && currentAdmin && (
                    <div className="bg-white dark:bg-zinc-900 p-4 sm:p-5 rounded-3xl border border-zinc-100 dark:border-zinc-800 shadow-sm space-y-4">
                        <div className="flex items-center gap-2 border-b border-zinc-100 dark:border-zinc-800 pb-3">
                            <BookOpen className="w-4 h-4 text-primary" />
                            <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">Session Selectors</h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 pl-1">Batch</label>
                                <select 
                                    value={selectedBatchId}
                                    onChange={(e) => setSelectedBatchId(e.target.value)}
                                    className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-750 px-3.5 py-2.5 rounded-2xl text-xs font-bold text-zinc-700 dark:text-zinc-200 outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                                >
                                    {batches.map(b => (
                                        <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                                    ))}
                                    {batches.length === 0 && <option value="">No batches configured</option>}
                                </select>
                            </div>

                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 pl-1">Subject</label>
                                <select 
                                    value={selectedSubjectId}
                                    onChange={(e) => setSelectedSubjectId(e.target.value)}
                                    className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-750 px-3.5 py-2.5 rounded-2xl text-xs font-bold text-zinc-700 dark:text-zinc-200 outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                                >
                                    {filteredSubjects.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                    {filteredSubjects.length === 0 && <option value="">No subjects found</option>}
                                </select>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 pl-1">Date</label>
                            <div className="relative">
                                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pl-0.5" />
                                <input 
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-750 pl-11 pr-4 py-2.5 rounded-2xl text-xs font-bold text-zinc-700 dark:text-zinc-200 outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Search & Filter Controls ─────────────────────── */}
                {session && (
                    <div className="space-y-4">
                        {/* Search Input */}
                        <div className="relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 transition-colors" />
                            <input
                                type="text"
                                placeholder="Find student by name or ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 focus:border-primary/30 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none text-zinc-800 dark:text-zinc-100 transition-all shadow-sm"
                            />
                        </div>

                        {/* Filter Tabs */}
                        <div className="flex gap-2 pb-2 overflow-x-auto no-scrollbar -mx-1 px-1">
                            {[
                                { id: 'all', label: 'All', count: counts.total, color: 'primary' },
                                { id: 'present', label: 'Present', count: counts.present, color: 'emerald' },
                                { id: 'absent', label: 'Absent', count: counts.absent, color: 'rose' },
                                { id: 'late', label: 'Late', count: counts.late, color: 'zinc' },
                            ].map(f => (
                                <button
                                    key={f.id}
                                    onClick={() => setActiveFilter(f.id)}
                                    className={`
                                        flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap border
                                        ${activeFilter === f.id 
                                            ? `bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-950 border-zinc-900 dark:border-zinc-100 shadow-md shadow-zinc-900/10` 
                                            : 'bg-white dark:bg-zinc-900 text-zinc-400 dark:text-zinc-500 border-zinc-150 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-850'
                                        }
                                    `}
                                >
                                    {f.label}
                                    <span className={`px-2 py-0.5 rounded-md text-[9px] ${activeFilter === f.id ? 'bg-white/20 dark:bg-zinc-950/20 text-white dark:text-zinc-900' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'}`}>
                                        {f.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* ── Student List ────────────────────────── */}
                {session && (
                    <div className="grid gap-3">
                        {studentList.length === 0 ? (
                            <div className="py-20 text-center border-2 border-dashed border-zinc-200 dark:border-zinc-800 rounded-3xl">
                                <Users className="w-12 h-12 text-zinc-200 dark:text-zinc-800 mx-auto mb-4" />
                                <p className="text-zinc-400 dark:text-zinc-550 font-bold text-xs uppercase tracking-widest">No entries found</p>
                            </div>
                        ) : (
                            studentList.map(student => (
                                <StudentRow
                                    key={student.id}
                                    student={student}
                                    status={pendingChanges[student.id] !== undefined ? pendingChanges[student.id] : (records[student.id] || null)}
                                    isPending={pendingChanges[student.id] !== undefined}
                                    onToggle={handleToggle}
                                    isEditing={isEditing}
                                />
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* ── Fixed Bottom Actions Bar ────────────────── */}
            {session && (
                <div className="fixed bottom-6 left-4 right-4 md:bottom-8 md:left-6 md:right-6 z-40 animate-in fade-in slide-in-from-bottom-8 duration-500">
                    {!isEditing ? (
                        <div className="max-w-md mx-auto bg-zinc-900 dark:bg-zinc-950 rounded-3xl shadow-2xl p-2 flex items-center justify-between">
                            <div className="px-4 sm:px-6">
                                <p className="text-white text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] opacity-50">Portal Mode</p>
                                <p className="text-white text-sm sm:text-base font-bold leading-none mt-0.5">Read Only</p>
                            </div>
                            <button 
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-primary hover:bg-primary/95 text-primary-foreground px-5 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all"
                            >
                                <Edit className="w-4 h-4" />
                                Edit Attendance
                            </button>
                        </div>
                    ) : (
                        <div className="max-w-md mx-auto bg-zinc-900 dark:bg-zinc-950 rounded-3xl shadow-2xl p-2 flex items-center gap-2">
                            <button 
                                onClick={() => {
                                    setPendingChanges({});
                                    setIsEditing(false);
                                }}
                                className="flex-1 text-white hover:text-zinc-300 px-3 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all text-center"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-xs font-black uppercase tracking-widest active:scale-95 transition-all disabled:opacity-50"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <footer className="max-w-lg mx-auto px-6 py-12 text-center opacity-30 select-none">
                <WifiOff className="w-4 h-4 mx-auto mb-2 dark:text-zinc-400" />
                <p className="text-[10px] font-bold uppercase tracking-widest dark:text-zinc-400">Portal session is temporary</p>
            </footer>
        </div>
    );
}
